import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { ApplicationService, UserService } from '@vtnz/ui';
import { ApplicationSettings, UserTypeEnum } from '@vtnz/api';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

/**
 * Guard to protect authenticated routes
 * Redirects to login if user is not authenticated
 */
export const authGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  const userService = inject(UserService);
  const router = inject(Router);

  // Use the signal by calling it with ()
  if (userService.isAuthenticated()) {
    return true;
  }

  // Store the attempted URL for redirecting after login
  sessionStorage.setItem('redirectUrl', state.url);
  router.navigate(['/login']);
  return false;
};

/**
 * Guard to protect booking route
 * Redirects to home if booking feature is not enabled
 */
export const bookEnabledGuard: CanActivateFn = () => {
  const applicationService = inject(ApplicationService);
  const router = inject(Router);

  if (applicationService.isBookingEnabled()) {
    return true;
  }

  // Redirect to home if booking is not enabled
  router.navigate(['/home']);
  return false;
};

/**
 * Restricts Manage Vehicles for commercial users when the corresponding feature is disabled.
 * Consumers retain access to their own vehicles regardless of this commercial-only toggle.
 */
export const commercialManageVehiclesEnabledGuard: CanActivateFn = () => {
  const applicationService = inject(ApplicationService);
  const userService = inject(UserService);
  const router = inject(Router);
  const injector = inject(Injector);
  const settings$ = toObservable(applicationService.settings, { injector }).pipe(
    filter((settings): settings is ApplicationSettings => settings !== undefined),
    take(1)
  );

  return userService.loadUserContext().pipe(
    switchMap((userContext) => {
      if (!userContext) {
        router.navigate(['/home']);
        return of(false);
      }

      if (userContext.userType !== UserTypeEnum.Commercial) {
        return of(true);
      }

      return settings$.pipe(
        map(() => {
          if (applicationService.isCommercialManageVehiclesEnabled()) {
            return true;
          }
          router.navigate(['/home']);
          return false;
        })
      );
    }),
    catchError(() => {
      router.navigate(['/home']);
      return of(false);
    })
  );
};

/**
 * Guard to prevent authenticated users from accessing login page
 * Redirects to dashboard if user is already authenticated
 */
export const noAuthGuard: CanActivateFn = () => {
  const userService = inject(UserService);
  const router = inject(Router);

  // Use the signal by calling it with ()
  if (!userService.isAuthenticated()) {
    return true;
  }

  router.navigate(['/home']);
  return false;
};
